import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { z } from "zod";

import { type CartonCountByProductionGroup } from "@/schemas";
import { getAuditCountsByProductionGroups } from "@/api/audit-api";

const REFETCH_INTERVAL = 15 * 1000;

// move into @schemas
const cartonCountByProductionGroupSchema = z.object({
	productionGroupId: z.string(),
	open: z.number(),
	failed: z.number(),
});

const cartonCountsSchema = z.array(cartonCountByProductionGroupSchema);

const fetchCartonCounts = async (): Promise<CartonCountByProductionGroup[] | null> => {
	try {
		const data = await getAuditCountsByProductionGroups();

		// TODO: Move validation to API layer after end-to-end testing on all components
		const validatedData = cartonCountsSchema.enrich({ callSite: "fetchCartonCounts" }).safeParseWithTelemetry(data);

		if (!validatedData.success) {
			return null;
		}

		return validatedData.data;
	} catch {
		return null;
	}
};

type UseCartonCountByProductionGroupsOptions = Omit<
	UseQueryOptions<CartonCountByProductionGroup[] | null, Error, CartonCountByProductionGroup[] | null, string[]>,
	"queryKey" | "queryFn"
>;

export const useCartonCountByProductionGroups = (queryConfig: UseCartonCountByProductionGroupsOptions = {}) => {
	const query = useQuery({
		queryKey: ["carton-counts-by-production-group"],
		queryFn: fetchCartonCounts,
		refetchInterval: REFETCH_INTERVAL,
		staleTime: 10 * 1000,
		...queryConfig,
	});

	return {
		cartonCountsByProductionGroup: query.data,
		refresh: query.refetch,
		isLoading: query.isLoading,
		error: query.error,
	};
};
